import React, { useCallback, useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Check } from 'lucide-react';
import { useCurrency } from '../hooks/useCurrency';
const IDLE_DELAY_MS = 90000; // 90 seconds before idle popup
const EXIT_INTENT_MIN_TIME_MS = 60000; // Exit intent only after 60s on page
const MAX_SHOWS = 1;
const DISCOUNT_CODE = 'EXTRA20';
const DISCOUNTED_PRICE = 14;
const ORIGINAL_PRICE = 96; // Base original price (guide + bonuses)
const PRODUCT_IMAGE = 'https://cdn.magicpatterns.com/uploads/9kfBDM9XGTP6kXkwGVeVWH/kndd_hero_thenap2.png';
function getShowCount(): number {
  try {
    return parseInt(sessionStorage.getItem('discount_show_count') || '0', 10);
  } catch {
    return 0;
  }
}
function setStoredCount(n: number) {
  sessionStorage.setItem('discount_show_count', String(n));
}
export function DiscountPopup() {
  const [isOpen, setIsOpen] = useState(false);
  const [showCount, setShowCount] = useState(getShowCount);
  const [bump1, setBump1] = useState(false);
  const [bump2, setBump2] = useState(false);
  const {
    formatPrice
  } = useCurrency();
  const secondChanceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMaxed = showCount >= MAX_SHOWS;
  const isSecondShow = showCount >= 1;
  const currentTotal = DISCOUNTED_PRICE + (bump1 ? 9 : 0) + (bump2 ? 9 : 0);
  const currentOriginal = ORIGINAL_PRICE + (bump1 ? 14 : 0) + (bump2 ? 8 : 0);
  const showPopup = useCallback(() => {
    setShowCount(prev => {
      if (prev >= MAX_SHOWS) return prev;
      const next = prev + 1;
      setStoredCount(next);
      return next;
    });
    setIsOpen(true);
  }, []);
  // Guard: should we listen for triggers?
  const shouldListen = !isMaxed && !isOpen;
  const cancelAll = useCallback(() => {
    setStoredCount(MAX_SHOWS);
    setShowCount(MAX_SHOWS);
    setIsOpen(false);
    if (secondChanceTimer.current) clearTimeout(secondChanceTimer.current);
  }, []);
  // ─── Trigger 1: General idle timer (45s fallback) ───
  useEffect(() => {
    if (!shouldListen) return;
    const timer = setTimeout(showPopup, IDLE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [shouldListen, showPopup]);
  // ─── Trigger 2: Exit intent — mouse leaves viewport (desktop, after min time) ───
  const pageLoadTime = useRef(Date.now());
  useEffect(() => {
    if (!shouldListen) return;
    const handleMouseLeave = (e: MouseEvent) => {
      const timeOnPage = Date.now() - pageLoadTime.current;
      if (e.clientY <= 0 && timeOnPage >= EXIT_INTENT_MIN_TIME_MS) showPopup();
    };
    document.addEventListener('mouseleave', handleMouseLeave);
    return () => document.removeEventListener('mouseleave', handleMouseLeave);
  }, [shouldListen, showPopup]);
  // ─── Cancel permanently if user clicks any CTA ───
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('[data-cta]') || target.closest('a[href*="gemmamama"]') || target.closest('button[class*="bg-sage"]') || target.closest('button[class*="bg-terracotta"]')) {
        cancelAll();
      }
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [cancelAll]);
  // ─── Lock scroll when open ───
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);
  const handleClose = () => {
    setIsOpen(false);
    // If we haven't hit max, schedule a second chance
    if (showCount < MAX_SHOWS) {
      secondChanceTimer.current = setTimeout(() => {
        showPopup();
      }, SECOND_CHANCE_DELAY_MS);
    }
  };
  const getCheckoutUrl = () => {
    let baseUrl = 'https://www.gemmamama.com/cart/45389317111947:1';
    if (bump1 && bump2) {
      baseUrl = 'https://www.gemmamama.com/cart/46426343342219:1';
    } else if (bump1) {
      baseUrl = 'https://www.gemmamama.com/cart/46426337214603:1';
    } else if (bump2) {
      baseUrl = 'https://www.gemmamama.com/cart/46426346487947:1';
    }
    return `${baseUrl}?discount=${DISCOUNT_CODE}`;
  };
  const handleClaim = () => {
    if (typeof window !== 'undefined' && (window as any).fbq) {
      ;
      (window as any).fbq('track', 'InitiateCheckout', {
        content_name: 'Soft Hold — Extra 20% Off',
        value: currentTotal,
        currency: 'USD'
      });
    }
    window.open(getCheckoutUrl(), '_blank');
    cancelAll();
  };
  // Cleanup second chance timer on unmount
  useEffect(() => {
    return () => {
      if (secondChanceTimer.current) clearTimeout(secondChanceTimer.current);
    };
  }, []);
  return <AnimatePresence data-id="element-89">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.35
      }} onClick={handleClose} className="fixed inset-0 bg-brown/30 backdrop-blur-sm z-[60]" data-id="element-90" />

          {/* Modal */}
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-5 pointer-events-none" data-id="element-91">
            <motion.div initial={{
          opacity: 0,
          scale: 0.96,
          y: 16
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.96,
          y: 16
        }} transition={{
          duration: 0.45,
          ease: [0.25, 0.1, 0.25, 1]
        }} className="w-full max-w-[340px] overflow-hidden pointer-events-auto rounded-2xl relative border border-brown/[0.04]" style={{
          background: 'linear-gradient(168deg, #FFFFFF 0%, #FAF7F2 50%, #F5F0E8 100%)',
          boxShadow: '0 24px 80px rgba(61,50,41,0.14), 0 4px 20px rgba(61,50,41,0.06), 0 0 0 1px rgba(255,255,255,0.6) inset'
        }} data-id="element-92">
              {/* Hero lifestyle header with warm overlay */}
              <div className="relative h-36 overflow-hidden" data-id="element-93">
                <img src="https://cdn.magicpatterns.com/uploads/9kfBDM9XGTP6kXkwGVeVWH/kndd_hero_thenap2.png" alt="Mother holding her sleeping newborn" className="w-full h-full object-cover" style={{
              objectPosition: 'center 40%'
            }} loading="eager" data-id="element-94" />
                {/* Warm gradient overlay */}
                <div className="absolute inset-0" style={{
              background: 'linear-gradient(180deg, rgba(250,247,242,0.15) 0%, rgba(250,247,242,0.5) 60%, rgba(250,247,242,0.95) 100%)'
            }} data-id="element-95" />
                {/* Extra 20% OFF pill floating on image */}
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 px-4 py-1.5 rounded-full backdrop-blur-md" style={{
              background: 'rgba(61,50,41,0.75)',
              boxShadow: '0 2px 12px rgba(61,50,41,0.2)'
            }} data-id="element-96">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-cream/90 font-semibold" data-id="element-97">
                    Extra 20% OFF — Today Only
                  </p>
                </div>
              </div>

              {/* Close button */}
              <button onClick={handleClose} className="absolute top-2.5 right-2.5 z-20 w-7 h-7 flex items-center justify-center rounded-full bg-white/70 backdrop-blur-sm hover:bg-white/90 transition-colors duration-200 text-brown-light/40 hover:text-brown/60" data-id="element-98">
                <X className="w-3.5 h-3.5" strokeWidth={1.5} data-id="element-99" />
              </button>

              <div className="px-7 pt-5 pb-7 text-center" data-id="element-100">
                {/* Headline */}
                <h2 className="font-serif text-[1.4rem] text-brown leading-snug mb-2.5" data-id="element-101">
                  Your peace shouldn't
                  <br data-id="element-102" />
                  have to wait.
                </h2>

                {/* Body */}
                <p className="text-brown-light/55 text-[13px] leading-relaxed mb-5 max-w-[250px] mx-auto" data-id="element-103">
                  You are so close to a calmer postpartum. Grab Soft Hold now
                  for{' '}
                  <span className="font-semibold text-terracotta" data-id="element-104">
                    an extra 20% OFF
                  </span>{' '}
                  and start feeling like yourself again today.
                </p>

                {/* Price */}
                <div className="flex items-baseline justify-center gap-2 mb-1" data-id="element-105">
                  <motion.span key={currentOriginal} initial={{
                opacity: 0,
                y: -5
              }} animate={{
                opacity: 1,
                y: 0
              }} className="font-serif text-sm text-brown/20 line-through" data-id="element-106">
                    {formatPrice(currentOriginal)}
                  </motion.span>
                  <motion.span key={currentTotal} initial={{
                opacity: 0,
                scale: 0.95
              }} animate={{
                opacity: 1,
                scale: 1
              }} className="font-serif text-[2rem] font-bold text-terracotta tracking-tight leading-none" style={{
                textShadow: '0 2px 12px rgba(198,114,76,0.1)'
              }} data-id="element-107">
                    {formatPrice(currentTotal)}
                  </motion.span>
                </div>

                <p className="text-brown-light/25 text-[9px] uppercase tracking-[0.15em] mb-4" data-id="element-108">
                  Guide + 3 bonuses · Yours forever
                </p>

                {/* Order Bumps */}
                <div className="bg-brown/[0.02] border border-brown/[0.04] rounded-xl p-3 mb-5 text-left" data-id="element-109">
                  <p className="text-[10px] uppercase tracking-[0.15em] text-sage font-bold mb-2.5 text-center" data-id="element-110">
                    Bundle & Save
                  </p>

                  {/* Bump 1 */}
                  <div className="flex items-start gap-2.5 mb-3 cursor-pointer group" onClick={() => setBump1(!bump1)} data-id="element-111">
                    <div className={`w-4 h-4 rounded border shrink-0 mt-0.5 flex items-center justify-center transition-colors ${bump1 ? 'bg-sage border-sage' : 'border-brown/20 bg-white group-hover:border-sage/50'}`} data-id="element-112">
                      {bump1 && <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} data-id="element-113" />}
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-114">
                      <div className="flex items-baseline justify-between gap-2" data-id="element-115">
                        <p className="font-serif text-[13px] font-bold text-brown leading-tight truncate" data-id="element-116">
                          Emergency Calm Cards
                        </p>
                        <span className="text-[11px] font-bold text-sage shrink-0" data-id="element-117">
                          +{formatPrice(9)}
                        </span>
                      </div>
                      <p className="text-[10px] text-brown-light/60 leading-snug mt-0.5" data-id="element-118">
                        10 Calm Cards for instant in-the-moment resets
                      </p>
                    </div>
                  </div>

                  {/* Divider */}
                  <div className="h-px bg-brown/[0.04] mb-3 ml-6" data-id="element-119" />

                  {/* Bump 2 */}
                  <div className="flex items-start gap-2.5 cursor-pointer group" onClick={() => setBump2(!bump2)} data-id="element-120">
                    <div className={`w-4 h-4 rounded border shrink-0 mt-0.5 flex items-center justify-center transition-colors ${bump2 ? 'bg-sage border-sage' : 'border-brown/20 bg-white group-hover:border-sage/50'}`} data-id="element-121">
                      {bump2 && <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} data-id="element-122" />}
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-123">
                      <div className="flex items-baseline justify-between gap-2" data-id="element-124">
                        <p className="font-serif text-[13px] font-bold text-brown leading-tight truncate" data-id="element-125">
                          Sanctuary Shields
                        </p>
                        <span className="text-[11px] font-bold text-sage shrink-0" data-id="element-126">
                          +{formatPrice(9)}
                        </span>
                      </div>
                      <p className="text-[10px] text-brown-light/60 leading-snug mt-0.5" data-id="element-127">
                        Scripts to protect your peace from unsolicited advice
                      </p>
                    </div>
                  </div>
                </div>

                {/* CTA */}
                <div className="relative mb-4" data-id="element-128">
                  <div className="absolute -inset-0.5 bg-terracotta/10 rounded-xl blur-sm" data-id="element-129" />
                  <motion.button onClick={handleClaim} whileHover={{
                scale: 1.02
              }} whileTap={{
                scale: 0.98
              }} className="relative w-full py-3.5 rounded-xl font-bold text-[12px] uppercase tracking-[0.12em] text-white cursor-pointer transition-all duration-300" style={{
                background: 'linear-gradient(135deg, #c4856a 0%, #b07258 50%, #a8654d 100%)',
                boxShadow: '0 4px 14px rgba(198,114,76,0.28)'
              }} data-id="element-130">
                    CLAIM MY CALM NOW →
                  </motion.button>
                </div>

                {/* Dismiss */}
                <button onClick={handleClose} className="text-[10px] text-brown-light/25 hover:text-brown-light/40 transition-colors duration-200" data-id="element-131">
                  No thanks, I'll figure it out on my own
                </button>
              </div>
            </motion.div>
          </div>
        </>}
    </AnimatePresence>;
}